import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircularProgress,
  Typography,
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip as MuiTooltip, // recharts has a Tooltip too
} from "@mui/material";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import { useLocalize } from "../i18n";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import AlertsTable from "./AlertsTable";
import FieldAlerts from "./FieldAlerts";
import { alertKeys, fieldKeys, fieldStatsKeys } from "../api/queryKeys";
import { chartDateAxis, formatChartDate, niceAxis } from "../utils/chart";
import { isNilUuid } from "../utils/funcs";
import {
  setFieldsLayerKey,
  setTiles,
  setEditingEntity,
  setDrawingMode,
  setCurrentField,
  setTargetBounds,
} from "../reducers/MapSlice";
import {
  getFieldAlerts,
  getFieldStats,
  getFieldAcceptability,
  getIndexTile,
  reviewedAlerts,
  validateAlerts,
  deleteField,
} from "../api/FlaskAPI";
import { FieldCard } from "./MapPopup";
import { newId } from "../utils/id";

function renderThresholdDot(acceptabilityThreshold) {
  return (props) => {
    if (props.value < acceptabilityThreshold) {
      const cx = props.cx;
      const cy = props.cy;
      return (
        <path
          cx={cx}
          cy={cy}
          d={`M ${cx - 4} ${cy - 4} L ${cx + 4} ${cy + 4} M ${cx + 4} ${cy - 4} L ${cx - 4} ${cy + 4}`}
          stroke="red"
          strokeWidth={2}
        />
      );
    }
    return false;
  };
}

// lines are in English only; their keys are fixed.
function renderChartLine({ lineProps, acceptabilityThreshold, t }) {
  return (
    <Line
      name={t(`charts.lines.${lineProps.key}`, {
        defaultValue: lineProps.name,
      })}
      type="monotone"
      dataKey={lineProps.key}
      stroke={lineProps.color}
      strokeWidth={2}
      dot={
        acceptabilityThreshold != null
          ? renderThresholdDot(acceptabilityThreshold)
          : false
      }
    />
  );
}

const intro = { fontSize: 12, color: "text.secondary", mb: 1.5 };

// A short dash "the blue line".
function LineMark({ line }) {
  return (
    <span style={{ color: line?.color ?? "inherit", fontWeight: 700 }}>—</span>
  );
}

// What the chart sections show, in a sentence each.
function ChartsIntro({ chartData, acceptability }) {
  const { t } = useTranslation();
  if (acceptability) {
    return <Typography sx={intro}>{t("charts.acceptabilityIntro")}</Typography>;
  }
  const lines = chartData[0]?.lines_props || [];
  const mean = lines.find((line) => line.key === "mean");
  const expected = lines.find((line) => line.key === "model_mean");
  return (
    <Typography component="div" sx={intro}>
      <Trans
        i18nKey="charts.indicesIntro"
        components={{
          mean: <LineMark line={mean} />,
          expected: <LineMark line={expected} />,
        }}
      />
    </Typography>
  );
}

function FieldCharts({ chartData, loading, error, sos, eos, acceptability }) {
  const indices = useSelector((state) => state.indices || []);
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  return (
    <>
      {/* Loading state */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Typography color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {/* No data state */}
      {!loading && !error && chartData.length === 0 && (
        <Typography sx={{ py: 2, color: "text.secondary" }}>
          {t("charts.noData")}
        </Typography>
      )}

      {/* Charts */}
      {!loading && !error && chartData.length > 0 && (
        <Box>
          <ChartsIntro chartData={chartData} acceptability={acceptability} />
          {chartData.map((chart, index) => {
            const info = indices.find(
              (item) => item.short_name_en === chart.name_en,
            );
            const unit = acceptability ? null : localize(info, "unit") || null;
            const threshold = chart.acceptability_threshold;
            const axis = niceAxis(chart.min_y, chart.max_y);
            return (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  {localize(chart, "name")}
                  {info?.full_name_en && (
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: 11, color: "text.secondary" }}
                    >
                      {localize(info, "full_name")}
                    </Typography>
                  )}
                </Typography>
                {/* Charts read left to right, also in Arabic (time axis). */}
                <Box dir="ltr" sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chart.values}>
                      <CartesianGrid strokeDasharray="3 3" />

                      {/* add acceptability line */}
                      {threshold != null && (
                        <ReferenceLine
                          y={threshold}
                          stroke="red"
                          strokeDasharray="3 3"
                          // Each index has its own threshold: named on the
                          // line, its value on the Y axis.
                          label={{
                            value: t("charts.threshold"),
                            position: "insideBottomRight",
                            fill: "red",
                            fontSize: 10,
                          }}
                        />
                      )}

                      {/* add start of season line */}
                      <ReferenceLine
                        x={sos}
                        stroke="green"
                        strokeDasharray="3 3"
                        label={{ value: "SOS", fill: "green", fontSize: 10 }}
                      />

                      {/* add end of season line */}
                      <ReferenceLine
                        x={eos}
                        stroke="orange"
                        strokeDasharray="3 3"
                        label={{ value: "EOS", fill: "orange", fontSize: 10 }}
                      />

                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        {...chartDateAxis()}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        {...axis}
                        {...(threshold != null && axis.ticks
                          ? {
                              ticks: [
                                ...axis.ticks.filter(
                                  (t) =>
                                    Math.abs(t - threshold) >=
                                    (axis.ticks[1] - axis.ticks[0]) / 2,
                                ),
                                threshold,
                              ].sort((x, y) => x - y),
                              interval: 0,
                              tick: ({ x, y, payload }) => (
                                <text
                                  x={x}
                                  y={y}
                                  dy={3}
                                  textAnchor="end"
                                  fontSize={10}
                                  fill={
                                    payload.value === threshold ? "red" : "#666"
                                  }
                                  fontWeight={
                                    payload.value === threshold ? 700 : 400
                                  }
                                >
                                  {payload.value}%
                                </text>
                              ),
                            }
                          : { tick: { fontSize: 10 } })}
                        tickFormatter={
                          acceptability ? (value) => `${value}%` : undefined
                        }
                        label={
                          unit && {
                            value: unit,
                            angle: -90,
                            position: "insideLeft",
                            style: { fontSize: 10, fill: "#6b7280" },
                          }
                        }
                      />
                      <Tooltip
                        labelFormatter={formatChartDate}
                        contentStyle={{ direction: i18n.dir() }}
                      />
                      {chart.lines_props.map((lineProps) =>
                        renderChartLine({
                          lineProps,
                          acceptabilityThreshold: chart.acceptability_threshold,
                          t,
                        }),
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}

// One foldable block of the panel; closed blocks don't render their content.
function PanelSection({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ borderTop: "1px solid #eef1f4" }}>
      <ButtonBase
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.25,
          textAlign: "left",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>
        {count != null && (
          <Box
            component="span"
            sx={{
              px: 0.75,
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 600,
              bgcolor: "#eef1f4",
              color: "text.secondary",
            }}
          >
            {count}
          </Box>
        )}
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        )}
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pb: 1.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

export default function FieldPanel({ dialogMode = false, refreshDashbord }) {
  const queryClient = useQueryClient();
  const localize = useLocalize();
  const [alertActionLoading, setAlertActionLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAllDialogOpen, setShowAllDialogOpen] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const workspaces = useSelector((state) => state.mapState.workspaces);
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const mapBounds = useSelector((state) => state.mapState.mapBounds);
  const currentField = useSelector((state) => state.mapState.currentField);
  const areaUnit = useSelector((state) => state.settings.areaUnit);
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);

  // Keyed by field id: a slow response can't land under a different field.
  const fieldId = currentField?.id;

  const { data: fieldAlerts = null, isPending: alertsLoading } = useQuery({
    queryKey: alertKeys.field(fieldId),
    queryFn: ({ signal }) =>
      getFieldAlerts(fieldId, signal).then((r) => r.data),
    enabled: Boolean(fieldId),
  });

  const {
    data: indicesChartData = [],
    isPending: indicesChartsPending,
    isError: indicesChartsIsError,
  } = useQuery({
    queryKey: fieldStatsKeys.stats(fieldId),
    queryFn: ({ signal }) => getFieldStats(fieldId, signal).then((r) => r.data),
    enabled: Boolean(fieldId),
  });

  const {
    data: acceptabilityChartData = [],
    isPending: acceptabilityChartsPending,
    isError: acceptabilityChartsIsError,
  } = useQuery({
    queryKey: fieldStatsKeys.acceptability(fieldId),
    queryFn: ({ signal }) =>
      getFieldAcceptability(fieldId, signal).then((r) => r.data),
    enabled: Boolean(fieldId),
  });

  const indicesChartsError = indicesChartsIsError
    ? t("panel.statsFailed")
    : null;
  const acceptabilityChartsError = acceptabilityChartsIsError
    ? t("panel.statsFailed")
    : null;

  const handleAddAlertToWorkspace = async (alert) => {
    // Check tile limit for current workspace before adding
    if (workspaces[currentWorkspace].length >= tilesLimits.workspace_tiles) {
      toast.error(
        t("map.maxTiles", {
          max: tilesLimits.workspace_tiles,
          workspace: currentWorkspace,
        }),
      );
      return;
    }

    setAlertActionLoading(true);

    try {
      const tileParams = {
        index_id: alert.index_id,
        image_date: alert.date,
        bounds: mapBounds,
      };
      const res = await getIndexTile(tileParams);

      if (res.status === 200) {
        dispatch(
          setTiles({
            tileId: newId(),
            tileUrl: res.data.tile_url_template,
            bbox: res.data.bounds,
            tileName: res.data.imagename_en,
            tileUrlExpiresAt: res.data.expires_at,
            // Kept so the signed tile URL can be re-minted before it expires.
            tileParams: tileParams,
          }),
        );
      }
    } catch (error) {
      console.error("Error adding alert tile to workspace:", error);
      toast.error(t("panel.addTileFailed"));
    } finally {
      setAlertActionLoading(false);
    }
  };

  // return true when the change went through, so the list can clear its selection.
  const handleAlertAction = async (alertIds, action) => {
    setAlertActionLoading(true);

    try {
      if (action === "reviewed") {
        await reviewedAlerts({ alerts_ids: alertIds, reviewed: true });
      } else if (action === "unreviewed") {
        await reviewedAlerts({ alerts_ids: alertIds, reviewed: false });
      } else if (action === "invalidate") {
        await validateAlerts({ alerts_ids: alertIds, valid: false });
      } else if (action === "validate") {
        await validateAlerts({ alerts_ids: alertIds, valid: true });
      } else {
        throw new Error("Unsupported action");
      }

      // Refetches this panel's alerts in place, so the section doesn't blank.
      await queryClient.invalidateQueries({ queryKey: alertKeys.all });
      if (refreshDashbord) {
        // Fields page: invalidates the list and re-syncs the open field.
        await refreshDashbord();
      } else {
        queryClient.invalidateQueries({ queryKey: fieldKeys.all });
      }

      // refresh fields vector tiles
      dispatch(setFieldsLayerKey());
      return true;
    } catch (error) {
      console.error("Error updating alerts:", error);
      toast.error(t("panel.alertsFailed"));
      return false;
    } finally {
      setAlertActionLoading(false);
    }
  };

  const handleEditField = () => {
    dispatch(setTargetBounds(currentField.bounds));
    dispatch(
      setEditingEntity({
        id: currentField.id,
        properties: {
          ...currentField,
          other_crop: isNilUuid(currentField.crop_id)
            ? localize(currentField, "crop")
            : "",
        },
      }),
    );
    dispatch(setDrawingMode("field"));
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      const response = await deleteField(currentField.id);
      if (response.status === 200 || response.status === 204) {
        toast.success(t("panel.deleted"));
        setConfirmDeleteOpen(false);
        queryClient.invalidateQueries({ queryKey: fieldKeys.all });
        queryClient.invalidateQueries({ queryKey: alertKeys.all });
        queryClient.invalidateQueries({ queryKey: fieldStatsKeys.all });
        dispatch(setFieldsLayerKey());
        dispatch(setCurrentField(null));
      } else {
        const msg = response.data?.message || t("panel.deleteFailed");
        toast.error(msg);
      }
    } catch (error) {
      const msg = error.response?.data?.message || t("panel.deleteFailed");
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return currentField ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        px: 2,
        backgroundColor: "transparent",
        boxShadow: "none",
        height: dialogMode ? "calc(80vh - 120px)" : "auto",
        overflowY: dialogMode ? "auto" : "visible",
        overflowX: "hidden",
      }}
    >
      <ConfirmDeleteDialog
        elementId={currentField?.id}
        handleConfirmDelete={handleConfirmDelete}
        isDialogOpen={confirmDeleteOpen}
        handleOnClose={handleCloseConfirmDelete}
        deleting={deleting}
      />

      <Box sx={{ pb: 1 }}>
        <FieldCard
          field={currentField}
          areaUnit={areaUnit}
          width="100%"
          actions={
            !dialogMode && (
              <Box sx={{ display: "flex", flexShrink: 0 }}>
                <MuiTooltip title={t("panel.zoom")}>
                  <IconButton
                    size="small"
                    aria-label={t("panel.zoom")}
                    onClick={() =>
                      dispatch(setTargetBounds(currentField.bounds))
                    }
                  >
                    <CenterFocusStrongOutlinedIcon fontSize="small" />
                  </IconButton>
                </MuiTooltip>
                <MuiTooltip title={t("panel.edit")}>
                  <IconButton
                    size="small"
                    aria-label={t("panel.edit")}
                    onClick={handleEditField}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </MuiTooltip>
                <MuiTooltip title={t("panel.delete")}>
                  <IconButton
                    size="small"
                    aria-label={t("panel.delete")}
                    color="error"
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </MuiTooltip>
              </Box>
            )
          }
        />
      </Box>

      <PanelSection
        title={t("panel.alerts")}
        count={fieldAlerts?.counts?.unreviewed?.count}
        defaultOpen
      >
        <FieldAlerts
          alerts={fieldAlerts}
          loading={alertsLoading}
          busy={alertActionLoading}
          onAction={handleAlertAction}
          onShowOnMap={handleAddAlertToWorkspace}
          onShowAll={() => setShowAllDialogOpen(true)}
        />
      </PanelSection>

      {/* Show All Dialog */}
      <Dialog
        open={showAllDialogOpen}
        onClose={() => setShowAllDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {t("panel.allAlertsTitle", { id: currentField.id })}
          </Typography>
          <IconButton
            onClick={() => setShowAllDialogOpen(false)}
            size="small"
            aria-label={t("common.close")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <AlertsTable
            fieldId={currentField.id}
            onAlertsChanged={refreshDashbord}
          />
        </DialogContent>
      </Dialog>
      <PanelSection
        title={t("panel.acceptability")}
        count={acceptabilityChartData.length || null}
      >
        <FieldCharts
          acceptability
          chartData={acceptabilityChartData}
          loading={acceptabilityChartsPending}
          error={acceptabilityChartsError}
        />
      </PanelSection>
      <PanelSection
        title={t("panel.indices")}
        count={indicesChartData.length || null}
      >
        <FieldCharts
          chartData={indicesChartData}
          loading={indicesChartsPending}
          error={indicesChartsError}
          sos={currentField.sos}
          eos={currentField.eos}
        />
      </PanelSection>
    </Box>
  ) : (
    <Box sx={{ textAlign: "center", py: 10, px: 3 }}>
      <Typography
        variant="h6"
        sx={{ color: "primary.main", fontWeight: 700, mb: 1 }}
      >
        {t("panel.explorerTitle")}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("panel.explorerHint")}
      </Typography>
    </Box>
  );
}
